import { DeepPartial } from '../../../test/react/utils/typescript-utils';
import { UserStore } from '../../js/stores/user-store';
import { CerbosFeature } from '../services/cerbos/features';
import { assertNever } from '../utils/variables';

type ProductionDomains =
  | 'portalen.skymap.se'
  | 'skymap-portal.com'
  | 'moba.skymap.se'
  | 'moba-de.skymap-portal.com'
  | 'moba-converter.skymap.se'
  | 'cad.skymap.se'
  | 'zynka.skymap.se'
  | 'zynka.skymap-portal.com'
  | 'cf-portalen.skymap.se'
  | 'cf-moba.skymap.se';
type DevelopmentDomains =
  | 'development.skymap.se'
  | 'development.skymap-portal.com'
  | 'mobadev.skymap.se'
  | 'moba-de-dev.skymap-portal.com'
  | 'caddev.skymap.se'
  | 'mobadev-converter.skymap.se'
  | 'zynka-dev.skymap-portal.com'
  | 'zynkadev.skymap.se'
  | 'cf-development.skymap.se'
  | 'cf-mobadev.skymap.se';
type LocalDomains = 'zynka.local' | 'moba-de.local' | 'moba.local' | 'dev.local' | 'localhost';

type AvailableDomains = ProductionDomains | DevelopmentDomains | LocalDomains;

type IntercomApp = {
  id: string;
  apiBase: string;
};

interface Theme {
  baseDir: string;
  themeName: 'default' | 'moba' | 'zynka';
  productName: string;
  termsOfServiceUrl: string;
  standardPriceListUrl: string;
  intercomApp: IntercomApp;
}

export type ThemeSettings = DeepPartial<{
  angularStates: {
    'sky.admin': boolean;
    'sky.admin.accessTokens': boolean;
    'sky.admin.accounts': boolean;
    'sky.admin.companyprojects': boolean;
    'sky.admin.joblist': boolean;
    'sky.admin.projects': boolean;
    'sky.admin.users.add': boolean;
    'sky.admin.users.invitecompanyadmin': boolean;
    'sky.admin.users.overview': boolean;
    'sky.admin.users.overview.edit': boolean;
    'sky.companyregister': boolean;
    'sky.companyregisterdesign': boolean;
    'sky.dashboard': boolean;
    'sky.access-management': boolean;
    'sky.gcp': boolean;
    'sky.geodataupload': boolean;
    'sky.newcompanyadmin': boolean;
    'sky.project': boolean;
    'sky.project.details': boolean;
    'sky.project.details.email': boolean;
    'sky.project.details.filemanager': boolean;
    'sky.project.details.geodata': boolean;
    'sky.project.details.geodata.setgcp': boolean;
    'sky.project.details.integrations': boolean;
    'sky.project.details.projectusers': boolean;
    'sky.project.details.settings': boolean;
    'sky.project.details.timeline': boolean;
    'sky.project.details.transfer': boolean;
    'sky.project.details.userlist': boolean;
    'sky.projecttransfer': boolean;
    'sky.shared-folder': boolean;
    'sky.sharedview': boolean;
    'sky.verify': boolean;
    'sky.viewer': boolean;
  };
  skyview: {
    toolbar: {
      centerViewVisible: boolean;
      commentVisible: boolean;
      compareOrthophotosVisible: boolean;
      inspectOriginalPhotosVisible: boolean;
      lightVisible: boolean;
      measureAdvancedVisible: boolean;
      measureVisible: boolean;
      occlusionCullingVisible: boolean;
      orthoSceneVisible: boolean;
      perspectiveSceneVisible: boolean;
      pointCloudSceneVisible: boolean;
      referencePlaneVisible: boolean;
      rotateVisible: boolean;
      scaleVisible: boolean;
      selectVisible: boolean;
      skyMapDesignVisible: boolean;
      timelinesVisible: boolean;
      wmsVisible: boolean;
      translateVisible: boolean;
      volumeCalculationVisible: boolean;
    };
    chips: {
      shareVisible: boolean;
      asBuiltVisible: boolean;
    };
    wms: {
      layers: {
        satellite: boolean;
      };
    };
  };
  fileManager: {
    actionBar: {
      connectVisible: boolean;
      folderTagsVisible: boolean;
      ConnectFolderVisible: boolean;
    };
  };
  other: {
    contextMenuVisible: boolean;
    intercomWidgetVisible: boolean;
    sharedViewTopBarVisible: boolean;
    supportIconVisible: boolean;
  };
}>;

export type AngularState = keyof Exclude<ThemeSettings['angularStates'], undefined>;

export class ThemeManager {
  protected static _instance = new ThemeManager();
  private _theme!: Theme;
  private _isSharedView = false;

  static get instance() {
    return this._instance;
  }

  private themeSettings?: ThemeSettings;

  get theme() {
    return this._theme;
  }

  get isSharedView() {
    return this._isSharedView;
  }

  get settings(): ThemeSettings {
    return this.themeSettings!;
  }

  constructor() {
    this.reinitialize();
    this.initSettings(false);
  }

  reinitialize() {
    this._theme = {
      baseDir: this.getProductType() === 'Portal' ? '/static/themes' : '/theming',
      themeName: this.getThemeName(),
      termsOfServiceUrl: this.getTermsOfServiceUrl(),
      standardPriceListUrl: this.getStandardPriceListUrl(),
      productName: this.getProductName(),
      intercomApp: this.getIntercomApp(),
    };

    this.addThemeNameToDom();
    this.setPageTitle();
    this.setFavIcon();
  }

  /**
   * Checks if it is a Swedish domain by excluding the domains we know
   * are outside of Sweden. This will be more all encompassing when
   * the viewer is run through an iframe.
   */
  isSwedishDomain() {
    switch (this.getHostname()) {
      case 'moba-de-dev.skymap-portal.com':
      case 'moba-de.skymap-portal.com':
      case 'moba-de.local':
        return false;
      default:
        return true;
    }
  }

  isMobaTheme() {
    return ThemeManager.instance.getThemeName() === 'moba';
  }

  getThemeName() {
    switch (this.getHostname()) {
      // Moba
      case 'moba.local':
      case 'moba.skymap.se':
      case 'cf-moba.skymap.se': // Temporary CloudFront DNS
      case 'moba-converter.skymap.se':
      case 'mobadev.skymap.se':
      case 'cf-mobadev.skymap.se': // Temporary CloudFront DNS
      case 'mobadev-converter.skymap.se':
      case 'moba-de-dev.skymap-portal.com':
      case 'moba-de.skymap-portal.com':
      case 'moba-de.local':
        return 'moba';

      // Zynka
      case 'zynka.local':
      case 'zynka.skymap.se':
      case 'zynkadev.skymap.se':
      case 'zynka.skymap-portal.com':
      case 'zynka-dev.skymap-portal.com':
        return 'zynka';

      // SkyMap
      default:
        return 'default';
    }
  }

  /**
   * Returns the URL to English terms of service document for MOBA Germany. Otherwise the URL to
   * Swedish terms of service document.
   */
  getTermsOfServiceUrl() {
    return this.hostnameStartsWith('moba-de')
      ? 'https://skymap.se/terms-portal'
      : 'https://skymap.se/villkor-portal';
  }

  getStandardPriceListUrl() {
    return 'https://www.skymap.se/priser-mp';
  }

  getHelpCenterUrl() {
    return 'https://intercom-help.eu/skymap/sv';
  }

  getLogoTypeUrl() {
    switch (this.getThemeName()) {
      case 'moba':
        return '/static/themes/moba/images/logotype.png';
      default:
        return '/static/themes/default/images/logotype.png';
    }
  }

  getHostname() {
    return window.location.hostname as AvailableDomains;
  }

  getProductType(): 'Portal' | 'Converter' {
    switch (this.getHostname()) {
      // Moba Design Tool.
      case 'moba.skymap.se':
      case 'cf-moba.skymap.se': // Temporary CloudFront DNS
      case 'mobadev.skymap.se':
      case 'moba-de-dev.skymap-portal.com':
      case 'moba-de.skymap-portal.com':
      case 'cf-mobadev.skymap.se': // Temporary CloudFront DNS
      case 'moba-de.local':
      case 'skymap-portal.com':
        return 'Portal';

      // Moba Converter.
      case 'moba-converter.skymap.se':
      case 'mobadev-converter.skymap.se':
        return 'Converter';

      // SkyMap Portal.
      case 'portalen.skymap.se':
      case 'cf-portalen.skymap.se': // Temporary CloudFront DNS
      case 'development.skymap.se':
      case 'cf-development.skymap.se': // Temporary CloudFront DNS
        return 'Portal';

      // SkyMap CAD-viewer.
      case 'cad.skymap.se':
      case 'caddev.skymap.se':
        return 'Converter';

      // Local development.
      case 'moba.local':
      case 'localhost':
        return window.location.port === '9090' ? 'Converter' : 'Portal';

      // Zynka does not have any theming and no CAD viewer.
      default:
        return 'Portal';
    }
  }

  getProductName() {
    switch (this.getThemeName()) {
      case 'moba':
        return this.getProductType() === 'Portal' ? 'MOBA Design Tool' : 'MOBA Converter';
      default:
        return this.getProductType() === 'Portal' ? 'SkyMap Portal' : 'SkyMap CAD viewer';
    }
  }

  getProductNamePortal() {
    switch (this.getThemeName()) {
      case 'moba':
        return 'MOBA Design Tool';
      default:
        return 'SkyMap Portal';
    }
  }

  getPortalUrl() {
    switch (this.getThemeName()) {
      case 'moba':
        return 'https://moba.skymap.se';
      default:
        return 'https://portalen.skymap.se';
    }
  }

  private getIntercomApp(): IntercomApp {
    switch (this.getThemeName()) {
      case 'moba':
        switch (this.getHostname()) {
          case 'moba.skymap.se':
          case 'cf-moba.skymap.se': // Temporary CloudFront DNS
            return { id: 'l3y5v1c7', apiBase: 'https://api-iam.intercom.io' };
          case 'moba-de.skymap-portal.com':
            return { id: 'mt2mwi41', apiBase: 'https://api-iam.intercom.io' };
          default:
            // Default api base to the same (US) as before.
            return { id: '', apiBase: 'https://api-iam.intercom.io' };
        }
      default:
        switch (this.getHostname()) {
          case 'dev.local':
          case 'development.skymap.se':
          case 'cf-development.skymap.se': // Temporary CloudFront DNS
          case 'caddev.skymap.se':
          case 'development.skymap-portal.com':
            return { id: 'tgg1ocnw', apiBase: 'https://api-iam.eu.intercom.io' };
          case 'portalen.skymap.se':
          case 'cf-portalen.skymap.se': // Temporary CloudFront DNS
          case 'cad.skymap.se':
          case 'skymap-portal.com':
            return { id: 'z4kpr0l0', apiBase: 'https://api-iam.eu.intercom.io' };
          default:
            // Default api base to the same (US) as before.
            return { id: '', apiBase: 'https://api-iam.intercom.io' };
        }
    }
  }

  getIntercomLastVisitedAttribute() {
    let attrName: 'skymap_portal_last_visited' | 'skymap_cad_viewer_last_visited' | undefined;

    if (this.getThemeName() === 'default') {
      switch (this.getHostname()) {
        // SkyMap Portal.
        case 'development.skymap.se':
        case 'cf-development.skymap.se': // Temporary CloudFront DNS
        case 'portalen.skymap.se':
        case 'cf-portalen.skymap.se': // Temporary CloudFront DNS
          attrName = 'skymap_portal_last_visited';
          break;

        // SkyMap CAD-viewer.
        case 'caddev.skymap.se':
        case 'cad.skymap.se':
          attrName = 'skymap_cad_viewer_last_visited';
          break;

        // Local development.
        // (localhost cannot be added as a trusted domain in Intercom)
        case 'dev.local':
          attrName =
            window.location.port === '8080'
              ? 'skymap_portal_last_visited'
              : 'skymap_cad_viewer_last_visited';
          break;
      }

      return attrName
        ? {
            [attrName]: new Date().getTime(),
          }
        : undefined;
    }
  }

  private addThemeNameToDom() {
    const themeName = this.theme.themeName === 'zynka' ? 'default' : this.theme.themeName;

    document.getElementsByTagName('html')[0].setAttribute('data-theme', themeName);
  }

  private setPageTitle() {
    document.title = this.theme.productName;
  }

  private setFavIcon() {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/x-icon';
    link.href = `${this.theme.baseDir}/${this.theme.themeName}/images/favicon.ico`;
    document.head.appendChild(link);
  }

  initSettings(isSharedView: boolean): void {
    switch (this.getThemeName()) {
      case 'default':
        this.themeSettings = {
          angularStates: {
            'sky.admin': true,
            'sky.admin.accessTokens': true,
            'sky.admin.accounts': true,
            'sky.admin.companyprojects': true,
            'sky.admin.joblist': true,
            'sky.admin.projects': true,
            'sky.admin.users.add': true,
            'sky.admin.users.invitecompanyadmin': true,
            'sky.admin.users.overview': true,
            'sky.admin.users.overview.edit': true,
            'sky.companyregister': true,
            'sky.companyregisterdesign': true,
            'sky.dashboard': true,
            'sky.access-management': true,
            'sky.gcp': true,
            'sky.geodataupload': true,
            'sky.newcompanyadmin': true,
            'sky.project': true,
            'sky.project.details': true,
            'sky.project.details.email': true,
            'sky.project.details.filemanager': true,
            'sky.project.details.geodata': true,
            'sky.project.details.geodata.setgcp': true,
            'sky.project.details.integrations': true,
            'sky.project.details.projectusers': true,
            'sky.project.details.settings': true,
            'sky.project.details.timeline': true,
            'sky.project.details.transfer': true,
            'sky.project.details.userlist': true,
            'sky.projecttransfer': true,
            'sky.shared-folder': true,
            'sky.sharedview': true,
            'sky.verify': true,
            'sky.viewer': true,
          },
          skyview: {
            toolbar: {
              centerViewVisible: true,
              commentVisible: !isSharedView,
              compareOrthophotosVisible: !isSharedView,
              inspectOriginalPhotosVisible: !isSharedView,
              lightVisible: true,
              measureAdvancedVisible: true,
              measureVisible: true,
              occlusionCullingVisible: true,
              orthoSceneVisible: true,
              perspectiveSceneVisible: true,
              pointCloudSceneVisible: true,
              referencePlaneVisible: true,
              rotateVisible: !isSharedView,
              scaleVisible: !isSharedView,
              selectVisible: true,
              skyMapDesignVisible: true,
              timelinesVisible: true,
              wmsVisible: true,
              translateVisible: !isSharedView,
              volumeCalculationVisible: !isSharedView,
            },
            chips: {
              shareVisible: true,
              asBuiltVisible: true,
            },
            wms: {
              layers: {
                satellite: true,
              },
            },
          },
          fileManager: {
            actionBar: {
              connectVisible: true,
              folderTagsVisible: true,
              ConnectFolderVisible: true,
            },
          },
          other: {
            intercomWidgetVisible: true,
            sharedViewTopBarVisible: true,
            supportIconVisible: true,
          },
        };
        break;

      case 'moba':
        this.themeSettings = {
          angularStates: {
            'sky.admin.companyprojects': true,
            'sky.admin.projects': true,
            'sky.admin.users.add': true,
            'sky.admin.users.invitecompanyadmin': true,
            'sky.admin.users.overview': true,
            'sky.admin.users.overview.edit': true,
            'sky.companyregister': true,
            'sky.companyregisterdesign': true,
            'sky.dashboard': true,
            'sky.access-management': true,
            'sky.newcompanyadmin': true,
            'sky.project': true,
            'sky.project.details': true,
            'sky.project.details.filemanager': true,
            'sky.project.details.integrations': true,
            'sky.project.details.projectusers': true,
            'sky.project.details.settings': true,
            'sky.shared-folder': true,
            'sky.verify': true,
            'sky.viewer': true,
          },
          skyview: {
            toolbar: {
              commentVisible: true,
              scaleVisible: true,
              selectVisible: true,
              skyMapDesignVisible: true,
              volumeCalculationVisible: true,
              lightVisible: true,
              wmsVisible: true,
              rotateVisible: true,
              translateVisible: true,
            },
            chips: {
              asBuiltVisible: true,
            },
            wms: {
              layers: {
                satellite: true,
              },
            },
          },
          fileManager: {
            actionBar: {
              connectVisible: true,
              folderTagsVisible: true,
              ConnectFolderVisible: true,
            },
          },
          other: {
            intercomWidgetVisible: true,
            sharedViewTopBarVisible: true,
          },
        };
        break;

      case 'zynka':
        this.themeSettings = {
          angularStates: {
            'sky.admin': true,
            'sky.admin.companyprojects': true,
            'sky.admin.projects': true,
            'sky.admin.users.add': true,
            'sky.admin.users.invitecompanyadmin': true,
            'sky.admin.users.overview.edit': true,
            'sky.companyregister': true,
            'sky.companyregisterdesign': true,
            'sky.dashboard': true,
            'sky.access-management': true,
            'sky.gcp': true,
            'sky.geodataupload': true,
            'sky.newcompanyadmin': true,
            'sky.project': true,
            'sky.project.details': true,
            'sky.project.details.email': true,
            'sky.project.details.filemanager': true,
            'sky.project.details.geodata': true,
            'sky.project.details.geodata.setgcp': true,
            'sky.project.details.integrations': true,
            'sky.project.details.projectusers': true,
            'sky.project.details.settings': true,
            'sky.project.details.timeline': true,
            'sky.project.details.transfer': true,
            'sky.project.details.userlist': true,
            'sky.projecttransfer': true,
            'sky.shared-folder': true,
            'sky.sharedview': true,
            'sky.verify': true,
            'sky.viewer': true,
          },
          skyview: {
            toolbar: {
              centerViewVisible: true,
              measureVisible: true,
              occlusionCullingVisible: true,
              orthoSceneVisible: true,
              perspectiveSceneVisible: true,
              pointCloudSceneVisible: true,
              wmsVisible: true,
              skyMapDesignVisible: true,
            },
            chips: {
              shareVisible: true,
              asBuiltVisible: true,
            },
            wms: {
              layers: {
                satellite: true,
              },
            },
          },
          fileManager: {
            actionBar: {
              connectVisible: true,
              folderTagsVisible: true,
              ConnectFolderVisible: true,
            },
          },
          other: {
            contextMenuVisible: true,
            supportIconVisible: true,
          },
        };
        break;
    }

    this._isSharedView = isSharedView;
  }

  hostnameStartsWith(value: string): boolean {
    return this.getHostname().startsWith(value);
  }

  hasAccessToState(stateName: AngularState) {
    if (UserStore.instance.isOrganizationAdmin()) {
      return true;
    }

    return Object.keys(this.settings.angularStates ?? {}).includes(stateName)
      ? this.settings.angularStates?.[stateName as keyof ThemeSettings['angularStates']]
      : false;
  }

  // Maps the cerbos feature key to a theme controlled propery,
  // and returns that property state.
  hasAccessToFeature(cerbosKey: CerbosFeature): boolean {
    const resolveCerbosKey = () => {
      switch (cerbosKey) {
        case 'feature:viewer:toolbar:comment':
          return this.themeSettings?.skyview?.toolbar?.commentVisible;
        case 'feature:viewer:toolbar:focus':
          return this.themeSettings?.skyview?.toolbar?.centerViewVisible;
        case 'feature:viewer:toolbar:select':
          return this.themeSettings?.skyview?.toolbar?.selectVisible;
        case 'feature:viewer:toolbar:translate':
          return this.themeSettings?.skyview?.toolbar?.translateVisible;
        case 'feature:viewer:toolbar:rotate':
          return this.themeSettings?.skyview?.toolbar?.rotateVisible;
        case 'feature:viewer:toolbar:scale':
          return this.themeSettings?.skyview?.toolbar?.scaleVisible;
        case 'feature:viewer:toolbar:light':
          return this.themeSettings?.skyview?.toolbar?.lightVisible;
        case 'feature:viewer:toolbar:volume_calculation':
          return this.themeSettings?.skyview?.toolbar?.volumeCalculationVisible;
        case 'feature:viewer:toolbar:measurement':
          return this.themeSettings?.skyview?.toolbar?.measureVisible;
        case 'feature:viewer:toolbar:measurement:advanced':
          return this.themeSettings?.skyview?.toolbar?.measureAdvancedVisible;
        case 'feature:viewer:toolbar:design':
          return this.themeSettings?.skyview?.toolbar?.skyMapDesignVisible;
        case 'feature:viewer:toolbar:tiled_model_scene':
          return this.themeSettings?.skyview?.toolbar?.perspectiveSceneVisible;
        case 'feature:viewer:toolbar:ortho_tile_scene':
          return this.themeSettings?.skyview?.toolbar?.orthoSceneVisible;
        case 'feature:viewer:toolbar:point_cloud_scene':
          return this.themeSettings?.skyview?.toolbar?.pointCloudSceneVisible;
        case 'feature:viewer:toolbar:compare_ortho_photos':
          return this.themeSettings?.skyview?.toolbar?.compareOrthophotosVisible;
        case 'feature:viewer:toolbar:original_photos':
          return this.themeSettings?.skyview?.toolbar?.inspectOriginalPhotosVisible;
        case 'feature:viewer:toolbar:occlusion_culling':
          return this.themeSettings?.skyview?.toolbar?.occlusionCullingVisible;
        case 'feature:viewer:toolbar:reference_plane':
          return this.themeSettings?.skyview?.toolbar?.referencePlaneVisible;
        case 'feature:viewer:toolbar:timeline':
          return this.themeSettings?.skyview?.toolbar?.timelinesVisible;
        case 'feature:viewer:toolbar:wms':
          return this.themeSettings?.skyview?.toolbar?.wmsVisible;
        case 'feature:viewer:wms:layers:satellite':
          return this.themeSettings?.skyview?.wms?.layers?.satellite;
        case 'feature:viewer:chips:share_viewer':
          return this.themeSettings?.skyview?.chips?.shareVisible;
        case 'feature:viewer:chips:as_built':
          return this.themeSettings?.skyview?.chips?.asBuiltVisible;
        case 'feature:shared_viewer:context_menu':
          return this.themeSettings?.other?.contextMenuVisible;
        case 'feature:global:intercom:widget':
          return this.themeSettings?.other?.intercomWidgetVisible;
        case 'feature:global:intercom:help_center':
          return this.themeSettings?.other?.supportIconVisible;
        case 'feature:file_manager:share_folder':
          return this.themeSettings?.fileManager?.actionBar?.connectVisible;
        case 'feature:file_manager:tags':
          return this.themeSettings?.fileManager?.actionBar?.folderTagsVisible;
        case 'feature:file_manager:connect_folder':
          return this.themeSettings?.fileManager?.actionBar?.ConnectFolderVisible;
        default:
          assertNever(cerbosKey);
      }
    };

    return resolveCerbosKey() ?? false;
  }
}
